import { enTetesAuth } from './enTetesAuth';
/**
 * Notifications Service - API calls + localStorage management
 *
 * Architecture:
 * - DB: 7 jours de rétention (nettoyage automatique)
 * - FileServer: 30 jours de rétention (JSON par utilisateur)
 * - LocalStorage: 30 jours de rétention (sync au login)
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be';
const STORAGE_KEY = 'cabaza_notifications';
const RETENTION_DAYS = 30;

/**
 * Get the current user's UUID from localStorage
 * @returns {string} User UUID or empty string
 */
const getUserUuid = () => {
    return localStorage.getItem('userUuid') || '';
};

// ============================================
// LocalStorage Management
// ============================================

/**
 * Get notifications from localStorage
 * @returns {Array} Notifications array
 */
export const getLocalNotifications = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return [];

        const notifications = JSON.parse(stored);
        // Filter out expired notifications (> 30 days)
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - RETENTION_DAYS);

        return notifications.filter(n => {
            const createdAt = new Date(n.createdAt || n.CREATEDAT);
            return createdAt >= cutoffDate;
        });
    } catch (error) {
        console.error('Error reading local notifications:', error);
        return [];
    }
};

/**
 * Save notifications to localStorage
 * @param {Array} notifications - Notifications to save
 */
export const saveLocalNotifications = (notifications) => {
    try {
        // Filter out expired notifications before saving
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - RETENTION_DAYS);

        const validNotifications = notifications.filter(n => {
            const createdAt = new Date(n.createdAt || n.CREATEDAT);
            return createdAt >= cutoffDate;
        });

        localStorage.setItem(STORAGE_KEY, JSON.stringify(validNotifications));
    } catch (error) {
        console.error('Error saving local notifications:', error);
    }
};

/**
 * Add a notification to localStorage
 * @param {object} notification - Notification to add
 */
export const addLocalNotification = (notification) => {
    const notifications = getLocalNotifications();
    // Check if already exists
    const id = notification.id || notification.ID;
    if (!notifications.some(n => (n.id || n.ID) === id)) {
        notifications.unshift(notification);
        saveLocalNotifications(notifications);
    }
};

/**
 * Update a notification in localStorage
 * @param {number} notificationId - ID of notification to update
 * @param {object} updates - Fields to update
 */
export const updateLocalNotification = (notificationId, updates) => {
    const notifications = getLocalNotifications();
    const index = notifications.findIndex(n => (n.id || n.ID) === notificationId);
    if (index !== -1) {
        notifications[index] = { ...notifications[index], ...updates };
        saveLocalNotifications(notifications);
    }
};

/**
 * Remove a notification from localStorage
 * @param {number} notificationId - ID of notification to remove
 */
export const removeLocalNotification = (notificationId) => {
    const notifications = getLocalNotifications();
    const filtered = notifications.filter(n => (n.id || n.ID) !== notificationId);
    saveLocalNotifications(filtered);
};

/**
 * Clear all local notifications
 */
export const clearLocalNotifications = () => {
    localStorage.removeItem(STORAGE_KEY);
};

// ============================================
// Sync API
// ============================================

/**
 * Sync notifications at login
 * Fetches missing notifications from DB + FileServer and merges with localStorage
 * @param {number} peopleId - User ID
 * @returns {Promise<Array>} Merged notifications
 */
export const syncNotifications = async (peopleId) => {
    try {
        const localNotifications = getLocalNotifications();
        const existingIds = localNotifications.map(n => n.id || n.ID);
        const lastId = existingIds.length > 0 ? Math.max(...existingIds) : 0;

        const response = await fetch(`${API_BASE}/rest/cabaza/notifications/sync`, {
            method: 'POST',
            headers: enTetesAuth(),
            body: JSON.stringify({
                peopleId,
                peopleUuid: getUserUuid(),
                lastNotificationId: lastId,
                existingIds
            })
        });

        const data = await response.json();
        const success = data.success ?? data.SUCCESS;

        if (success) {
            const newNotifications = data.notifications ?? data.NOTIFICATIONS ?? [];

            // Merge: add new notifications that don't exist locally
            const merged = [...localNotifications];
            for (const notif of newNotifications) {
                const notifId = notif.id || notif.ID;
                if (!existingIds.includes(notifId)) {
                    merged.push(notif);
                }
            }

            // Sort by date descending
            merged.sort((a, b) => {
                const dateA = new Date(a.createdAt || a.CREATEDAT);
                const dateB = new Date(b.createdAt || b.CREATEDAT);
                return dateB - dateA;
            });

            // Save merged list
            saveLocalNotifications(merged);

            return merged;
        }

        return localNotifications;
    } catch (error) {
        console.error('Error syncing notifications:', error);
        return getLocalNotifications();
    }
};

// ============================================
// API Calls
// ============================================

/**
 * Get notifications for a user
 * @param {number} peopleId - User ID
 * @param {object} options - Options (limit, offset, isRead, moduleCode)
 * @returns {Promise<Array>} List of notifications
 */
export const getNotifications = async (peopleId, options = {}) => {
    try {
        const params = new URLSearchParams({
            peopleId,
            limit: options.limit || 50,
            offset: options.offset || 0
        });

        if (options.isRead !== undefined && options.isRead !== '') {
            params.append('isRead', options.isRead);
        }
        if (options.moduleCode) {
            params.append('moduleCode', options.moduleCode);
        }

        const response = await fetch(`${API_BASE}/rest/cabaza/notifications?${params}`);
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const notifications = data.data ?? data.DATA ?? [];
        return success ? notifications : [];
    } catch (error) {
        console.error('Error fetching notifications:', error);
        return [];
    }
};

/**
 * Get unread notifications count
 * @param {number} peopleId - User ID
 * @returns {Promise<number>} Unread count
 */
export const getUnreadCount = async (peopleId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/notifications/unread-count?peopleId=${peopleId}`);
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const count = data.count ?? data.COUNT ?? 0;
        return success ? count : 0;
    } catch (error) {
        console.error('Error fetching unread count:', error);
        return 0;
    }
};

/**
 * Get unread count from localStorage
 * @returns {number} Unread count
 */
export const getLocalUnreadCount = () => {
    const notifications = getLocalNotifications();
    return notifications.filter(n => {
        const isRead = n.isRead ?? n.ISREAD;
        return !isRead;
    }).length;
};

/**
 * Mark a notification as read
 * @param {number} notificationId - Notification ID
 * @param {number} peopleId - User ID
 * @returns {Promise<object>} Result
 */
export const markAsRead = async (notificationId, peopleId) => {
    try {
        // Update localStorage immediately
        updateLocalNotification(notificationId, {
            isRead: true,
            ISREAD: true,
            readAt: new Date().toISOString(),
            READAT: new Date().toISOString()
        });

        // Then update server
        const uuid = getUserUuid();
        const response = await fetch(`${API_BASE}/rest/cabaza/notifications/${notificationId}/read?peopleId=${peopleId}&peopleUuid=${uuid}`, {
            method: 'PUT',
            headers: enTetesAuth(false)
        });
        return await response.json();
    } catch (error) {
        console.error('Error marking notification as read:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Mark a notification as unread
 * @param {number} notificationId - Notification ID
 * @param {number} peopleId - User ID
 * @returns {Promise<object>} Result
 */
export const markAsUnread = async (notificationId, peopleId) => {
    try {
        // Update localStorage immediately
        updateLocalNotification(notificationId, {
            isRead: false,
            ISREAD: false,
            readAt: null,
            READAT: null
        });

        // Then update server
        const uuid = getUserUuid();
        const response = await fetch(`${API_BASE}/rest/cabaza/notifications/${notificationId}/unread?peopleId=${peopleId}&peopleUuid=${uuid}`, {
            method: 'PUT',
            headers: enTetesAuth(false)
        });
        return await response.json();
    } catch (error) {
        console.error('Error marking notification as unread:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Mark all notifications as read
 * @param {number} peopleId - User ID
 * @returns {Promise<object>} Result
 */
export const markAllAsRead = async (peopleId) => {
    try {
        // Update localStorage immediately
        const notifications = getLocalNotifications();
        const now = new Date().toISOString();
        const updated = notifications.map(n => ({
            ...n,
            isRead: true,
            ISREAD: true,
            readAt: now,
            READAT: now
        }));
        saveLocalNotifications(updated);

        // Then update server
        const uuid = getUserUuid();
        const response = await fetch(`${API_BASE}/rest/cabaza/notifications/read-all?peopleId=${peopleId}&peopleUuid=${uuid}`, {
            method: 'PUT',
            headers: enTetesAuth(false)
        });
        return await response.json();
    } catch (error) {
        console.error('Error marking all notifications as read:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a notification
 * @param {number} notificationId - Notification ID
 * @param {number} peopleId - User ID
 * @returns {Promise<object>} Result
 */
export const deleteNotification = async (notificationId, peopleId) => {
    try {
        // Remove from localStorage immediately
        removeLocalNotification(notificationId);

        // Then delete from server
        const response = await fetch(`${API_BASE}/rest/cabaza/notifications/${notificationId}?peopleId=${peopleId}`, {
            method: 'DELETE',
            headers: enTetesAuth(false)
        });
        return await response.json();
    } catch (error) {
        console.error('Error deleting notification:', error);
        return { success: false, message: error.message };
    }
};

// ============================================
// Utility Functions
// ============================================

/**
 * Format notification time relative to now
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted time (e.g., "il y a 5 min", "hier", etc.)
 */
export const formatNotificationTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'a l\'instant';
    if (diffMins < 60) return `il y a ${diffMins} min`;
    if (diffHours < 24) return `il y a ${diffHours}h`;
    if (diffDays === 1) return 'hier';
    if (diffDays < 7) return `il y a ${diffDays} jours`;

    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

/**
 * Get notification icon based on type
 * @param {string} notificationType - Notification type
 * @returns {string} Icon name for lucide-react
 */
export const getNotificationIcon = (notificationType) => {
    const icons = {
        // MEETINGS
        'meeting_invitation': 'UserPlus',
        'meeting_updated': 'RefreshCw',
        'meeting_cancelled': 'XCircle',
        'meeting_confirmed': 'CheckCircle',
        'meeting_closed': 'Lock',
        'participant_accepted': 'UserCheck',
        'participant_declined': 'UserX',
        'participant_proposed': 'MessageSquare',
        'participant_withdrawn': 'UserMinus',
        // BOOKS
        'book_request': 'BookOpen',
        'book_request_accepted': 'CheckCircle',
        'book_request_declined': 'XCircle',
        'book_handover_confirmed': 'PackageCheck',
        'book_return_confirmed': 'PackageCheck',
        'book_reminder': 'Bell',
        'book_overdue': 'AlertTriangle',
        'book_overdue_owner': 'AlertTriangle',
        // MESSAGES
        'new_message': 'MessageCircle',
        'message_read': 'CheckCheck',
        // CONTACTS
        'contact_request': 'UserPlus',
        'contact_accepted': 'UserCheck',
        'contact_declined': 'UserX',
        // CALENDAR
        'event_invitation': 'Calendar',
        'event_reminder': 'Bell',
        'event_cancelled': 'CalendarX',
        // WORK
        'work_request_received': 'Briefcase',
        'work_request_response': 'MessageSquare',
        // SYSTEM
        'welcome': 'Sparkles',
        'subscription_expiring': 'Clock',
        'subscription_expired': 'AlertCircle',
        'account_security': 'Shield'
    };
    return icons[notificationType] || 'Bell';
};

/**
 * Get notification color based on module or type
 * @param {string} moduleCode - Module code
 * @param {string} notificationType - Notification type
 * @returns {string} Tailwind color class
 */
export const getNotificationColor = (moduleCode, notificationType = '') => {
    // Module-based colors
    const moduleColors = {
        'MEETINGS': 'text-rose-500',
        'BOOKS': 'text-amber-500',
        'MESSAGES': 'text-purple-500',
        'CONTACTS': 'text-blue-500',
        'CALENDAR': 'text-indigo-500',
        'WORK': 'text-blue-500',
        'SYSTEM': 'text-gray-500'
    };

    // Type-based overrides for specific states
    const typeColors = {
        'book_request_accepted': 'text-green-500',
        'book_request_declined': 'text-red-500',
        'book_overdue': 'text-red-500',
        'book_overdue_owner': 'text-red-500',
        'meeting_cancelled': 'text-red-500',
        'meeting_confirmed': 'text-green-500',
        'participant_declined': 'text-red-500',
        'contact_accepted': 'text-green-500',
        'contact_declined': 'text-red-500',
        'subscription_expired': 'text-red-500',
        'account_security': 'text-orange-500'
    };

    return typeColors[notificationType] || moduleColors[moduleCode] || 'text-gray-500';
};

/**
 * Get module icon name based on module code
 * @param {string} moduleCode - Module code (MEETINGS, BOOKS, etc.)
 * @returns {string} Icon name for lucide-react
 */
export const getModuleIcon = (moduleCode) => {
    const icons = {
        'MEETINGS': 'Users',
        'BOOKS': 'BookOpen',
        'MESSAGES': 'MessageCircle',
        'CONTACTS': 'Contact',
        'CALENDAR': 'Calendar',
        'EVENTS': 'CalendarDays',
        'PLACES': 'MapPin',
        'MAP': 'Map',
        'AGENDA': 'CalendarCheck',
        'SUPPORT': 'LifeBuoy',
        'WORK': 'Briefcase',
        'OFFERS': 'Tag',
        'PRODUCTS': 'Package',
        'KNOWLEDGE': 'GraduationCap',
        'EXPENSES': 'Receipt',
        'MOMENTS': 'Camera',
        'CARPOOLING': 'Car',
        'SYSTEM': 'Settings'
    };
    return icons[moduleCode] || 'Bell';
};

/**
 * Get navigation route for a notification
 * @param {object} notification - Notification object
 * @returns {object|null} Route object with page and entityId, or null
 */
export const getNotificationRoute = (notification) => {
    const moduleCode = notification.moduleCode || notification.MODULECODE;
    const entityId = notification.entityId || notification.ENTITYID;
    const entityType = notification.entityType || notification.ENTITYTYPE;

    if (!entityId) return null;

    switch (moduleCode) {
        case 'MEETINGS':
            return { page: 'meetings', entityId };
        case 'BOOKS': {
            let tabName = 'myBooks';
            const notifType = notification.notificationType || notification.NOTIFICATIONTYPE || '';
            const requesterNotifTypes = ['book_request_accepted', 'book_request_declined', 'book_request_cancelled'];
            if (entityType === 'Book_Requests') {
                tabName = requesterNotifTypes.includes(notifType) ? 'requestsSent' : 'requestsReceived';
            } else if (entityType === 'Book_Transactions') {
                tabName = 'transactions';
            }
            return { page: 'books', entityId, entityType, tabName };
        }
        case 'MESSAGES':
            return { page: 'messages', entityId };
        case 'CONTACTS':
            return { page: 'contacts', entityId };
        case 'CALENDAR':
            return { page: 'calendar', entityId };
        case 'WORK':
            return { page: 'work', entityId };
        default:
            return null;
    }
};
